import { Instagram, ArrowUpRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useLocale } from "@/i18n/LocaleContext";
import { INSTAGRAM, localePath } from "@/i18n/translations";
import { privacyContent, privacyPath } from "@/i18n/privacy";

export const Footer = () => {
  const { t, locale } = useLocale();
  const { pathname } = useLocation();
  const home = localePath[locale] || "/";
  const onLegal = pathname.endsWith("/legal");
  const hashHref = (h) => (onLegal ? `${home}${h}` : h);
  const legalHref = home === "/" ? "/legal" : `${home}/legal`;
  const privacy = privacyContent[locale] || privacyContent.en;

  const exploreLinks = [
    [t.nav.solutions, hashHref("#dual"), "solutions"],
    [t.nav.services, hashHref("#services"), "services"],
    [t.nav.business, hashHref("#business"), "business"],
    [t.nav.ventures, hashHref("#ventures"), "ventures"],
    [t.nav.about, hashHref("#about"), "about"],
    [t.nav.contact, hashHref("#contact"), "contact"],
  ];

  return (
    <footer className="relative border-t border-white/10 pt-20 pb-10 overflow-hidden" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-3 gap-12 mb-20">
          <div className="md:col-span-1">
            <p className="font-playfair text-2xl leading-snug max-w-xs">{t.footer.tagline}</p>
            <a
              href={INSTAGRAM.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-ig-link"
              className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-nowi-muted hover:text-nowi-venture transition-colors"
            >
              <Instagram className="w-4 h-4" strokeWidth={1.5} /> {INSTAGRAM.handle}
            </a>
          </div>

          <div className="grid grid-cols-2 gap-8 md:col-span-2">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-5">{t.footer.exploreLabel}</div>
              <ul className="space-y-3">
                {exploreLinks.map(([l, h, id]) => (
                  <li key={id}>
                    <a href={h} className="text-nowi-muted hover:text-nowi-text transition-colors text-sm" data-testid={`footer-${id}-link`}>{l}</a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-5">{t.footer.connectLabel}</div>
              <ul className="space-y-3 text-sm">
                <li><a href="mailto:hello@nowi.digital" className="text-nowi-muted hover:text-nowi-text transition-colors">hello@nowi.digital</a></li>
                <li><a href="#contact" className="inline-flex items-center gap-1 text-nowi-muted hover:text-nowi-text transition-colors">{t.footer.startProject} <ArrowUpRight className="w-3 h-3" strokeWidth={1.5} /></a></li>
                <li><a href={hashHref("#book")} className="inline-flex items-center gap-1 text-nowi-muted hover:text-nowi-text transition-colors" data-testid="footer-book-link">{t.consult.kicker} <ArrowUpRight className="w-3 h-3" strokeWidth={1.5} /></a></li>
                <li><Link to={legalHref} className="text-nowi-muted hover:text-nowi-text transition-colors" data-testid="footer-legal-link">{t.legal.footerLink}</Link></li>
                <li><Link to={privacyPath(locale)} className="text-nowi-muted hover:text-nowi-text transition-colors" data-testid="footer-privacy-link">{privacy.footerLink}</Link></li>
                <li className="text-nowi-muted">{t.contact.studioValue}</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-10">
          <div className="font-syncopate font-bold text-[clamp(3rem,16vw,14rem)] leading-none tracking-[0.02em] text-nowi-text select-none">
            NOWI<span className="text-nowi-tech">.</span>
          </div>
          <div className="mt-8 flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4 font-mono text-[10px] uppercase tracking-[0.2em] text-nowi-muted">
            <span data-testid="footer-company">NOWI Skills AB · Org.nr 559416-0227</span>
            <span>{t.footer.rights}</span>
            <span>Stockholm 59.33°N · 18.06°E</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
